import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useResizableColumns from "../hooks/useResizableColumns";

const STATO_CONFIG = {
  100000000: { label: "Aperta", class: "bg-red-100 text-red-700" },
  100000001: { label: "In corso", class: "bg-yellow-100 text-yellow-700" },
  100000002: { label: "Completata", class: "bg-green-100 text-green-700" },
  100000003: { label: "Verificata", class: "bg-blue-100 text-blue-700" }, // legacy
  100000004: { label: "Conclusa", class: "bg-gray-100 text-gray-600" },
};

const PRIORITA_CONFIG = {
  100000000: { label: "Bassa", class: "text-gray-500" },
  100000001: { label: "Media", class: "text-yellow-600" },
  100000002: { label: "Alta", class: "text-orange-600 font-medium" },
  100000003: { label: "Critica", class: "text-red-600 font-bold" },
};

function AzioniCorrettiveListPage() {
  const { user, apiRequest, canAccessSection } = useAuth();
  const [azioni, setAzioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statoFilter, setStatoFilter] = useState("");
  const [rsaFilter, setRsaFilter] = useState("");
  const [assegnatoFilter, setAssegnatoFilter] = useState("");
  const [rsaList, setRsaList] = useState([]);
  const [assegnatoList, setAssegnatoList] = useState([]);

  const isFornitore = user?.ruolo === 100000005;

  const { onMouseDown, headerStyle, cellStyle } = useResizableColumns({
    descrizione: 250,
    intervento: 200,
    rsa: 140,
    assegnato: 150,
    priorita: 90,
    scadenza: 110,
    stato: 110,
    azioni: 80,
  });

  useEffect(() => {
    if (!isFornitore) {
      apiRequest("/rsa")
        .then((res) => setRsaList(res?.value || []))
        .catch(() => setRsaList([]));
    }
  }, [apiRequest, isFornitore]);

  useEffect(() => {
    apiRequest("/azioni-correttive/stats")
      .then(setStats)
      .catch(() => {});
    // Fetch all azioni once to extract distinct assegnato values
    apiRequest("/azioni-correttive")
      .then((res) => {
        const names = (res.value || [])
          .map((a) => a.soggettoAssegnato)
          .filter(Boolean);
        setAssegnatoList([...new Set(names)].sort());
      })
      .catch(() => {});
  }, [apiRequest]);

  useEffect(() => {
    const fetchAzioni = async () => {
      try {
        const params = new URLSearchParams();
        if (statoFilter) params.append("stato", statoFilter);
        if (rsaFilter) params.append("rsaId", rsaFilter);
        if (assegnatoFilter) params.append("assegnato", assegnatoFilter);
        const result = await apiRequest(
          `/azioni-correttive?${params.toString()}`,
        );
        setAzioni(result.value || []);
      } catch (error) {
        console.error("Error fetching azioni correttive:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAzioni();
  }, [apiRequest, statoFilter, rsaFilter, assegnatoFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Azioni Correttive</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div className="card text-center py-3">
            <div className="text-xl font-bold">{stats.totale}</div>
            <div className="text-xs text-gray-500">Totale</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xl font-bold text-red-600">{stats.aperte}</div>
            <div className="text-xs text-gray-500">Aperte</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xl font-bold text-yellow-600">
              {stats.inCorso}
            </div>
            <div className="text-xs text-gray-500">In corso</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xl font-bold text-green-600">
              {stats.completate}
            </div>
            <div className="text-xs text-gray-500">Completate</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xl font-bold text-gray-500">
              {stats.concluse}
            </div>
            <div className="text-xs text-gray-500">Concluse</div>
          </div>
          <div className="card text-center py-3">
            <div
              className={`text-xl font-bold ${stats.scadute > 0 ? "text-red-600" : "text-gray-400"}`}
            >
              {stats.scadute}
            </div>
            <div className="text-xs text-gray-500">Scadute</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap">
          <select
            value={statoFilter}
            onChange={(e) => setStatoFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">Tutti gli stati</option>
            <option value="100000000">Aperte</option>
            <option value="100000001">In corso</option>
            <option value="100000002">Completate</option>
            <option value="100000004">Concluse</option>
          </select>
          {!isFornitore && (
            <select
              value={rsaFilter}
              onChange={(e) => setRsaFilter(e.target.value)}
              className="input w-48"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          )}
          <select
            value={assegnatoFilter}
            onChange={(e) => setAssegnatoFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">Tutti gli assegnatari</option>
            {assegnatoList.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {azioni.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {[
                    { key: "descrizione", label: "Descrizione" },
                    { key: "intervento", label: "Intervento" },
                    ...(!isFornitore ? [{ key: "rsa", label: "RSA" }] : []),
                    { key: "assegnato", label: "Assegnato a" },
                    { key: "priorita", label: "Priorita" },
                    { key: "scadenza", label: "Scadenza" },
                    { key: "stato", label: "Stato" },
                    { key: "azioni", label: "" },
                  ].map((col) => (
                    <th key={col.key} style={headerStyle(col.key)}>
                      <div className="flex items-center justify-between">
                        <span>{col.label}</span>
                        <span
                          onMouseDown={(e) => {
                            onMouseDown(col.key, e);
                            e.target.style.background = "#3b82f6";
                          }}
                          style={{
                            position: "absolute",
                            right: -1,
                            top: 4,
                            bottom: 4,
                            width: 3,
                            cursor: "col-resize",
                            borderRadius: 2,
                            background: "#d1d5db",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#3b82f6";
                            e.target.style.width = "4px";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#d1d5db";
                            e.target.style.width = "3px";
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {azioni.map((a) => {
                  const statoConf =
                    STATO_CONFIG[a.stato] || STATO_CONFIG[100000000];
                  const prioConf =
                    PRIORITA_CONFIG[a.priorita] || PRIORITA_CONFIG[100000001];
                  const isScaduta = a.scaduta;

                  return (
                    <tr key={a.id} className={isScaduta ? "bg-red-50" : ""}>
                      <td
                        style={cellStyle("descrizione")}
                        title={a.descrizione}
                      >
                        {a.descrizione?.substring(0, 80)}
                        {a.descrizione?.length > 80 ? "..." : ""}
                      </td>
                      <td style={cellStyle("intervento")}>
                        {a.intervento ? (
                          <Link
                            to={`/interventi/${a.intervento.id}`}
                            className="text-primary-600 hover:underline"
                          >
                            {a.intervento.nome}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      {!isFornitore && (
                        <td style={cellStyle("rsa")} title={a.rsa?.nome}>
                          {a.rsa?.nome || "-"}
                        </td>
                      )}
                      <td
                        style={cellStyle("assegnato")}
                        title={a.soggettoAssegnato}
                      >
                        {a.soggettoAssegnato || "-"}
                      </td>
                      <td style={cellStyle("priorita")}>
                        <span className={prioConf.class}>{prioConf.label}</span>
                      </td>
                      <td style={cellStyle("scadenza")}>
                        <span
                          className={
                            isScaduta ? "text-red-600 font-medium" : ""
                          }
                        >
                          {a.scadenza
                            ? new Date(a.scadenza).toLocaleDateString("it-IT")
                            : "-"}
                        </span>
                        {isScaduta && (
                          <span className="block text-xs text-red-500">
                            Scaduta
                          </span>
                        )}
                      </td>
                      <td style={cellStyle("stato")}>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${statoConf.class}`}
                        >
                          {statoConf.label}
                        </span>
                      </td>
                      <td style={cellStyle("azioni")}>
                        <Link
                          to={`/azioni-correttive/${a.id}`}
                          className="text-primary-600 hover:underline text-sm"
                        >
                          Dettagli
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Nessuna azione correttiva trovata
          </p>
        )}
      </div>
    </div>
  );
}

export default AzioniCorrettiveListPage;
